import React, { FC, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import {
  ReasonOption,
  testTypeToReasonOption,
} from "@app/utils/intakeFormOptions";
import { IntakeFormStepLayout, InlineCheckbox } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepReason.title",
    defaultMessage:
      "What's the reason for making this appointment? You can chose multiple answers.",
  },
  optionSignatera: {
    id: "intakeStepReason.optionSignatera",
    defaultMessage: "Signatera",
  },
  optionAltera: {
    id: "intakeStepReason.optionAltera",
    defaultMessage: "Altera",
  },
  optionLatitude: {
    id: "intakeStepReason.optionLatitude",
    defaultMessage: "Latitude",
  },
  optionEmpower: {
    id: "intakeStepReason.optionEmpower",
    defaultMessage: "Empower",
  },
  nextButton: {
    id: "intakeStepReason.nextButton",
    defaultMessage: "Next",
  },
});

const IntakeStepReason: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    userInputFields,
    updateUserField,
    setCurrentStep,
    totalSteps,
    prefilledData,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.REASON;

  const parseSelectedOptions = (value: string | null): Set<ReasonOption> => {
    if (!value) return new Set();
    return new Set(value.split(",") as ReasonOption[]);
  };

  const [selectedOptions, setSelectedOptions] = useState<Set<ReasonOption>>(
    () => {
      const fromUserInput = parseSelectedOptions(
        userInputFields.reasonForAppointment
      );
      if (fromUserInput.size > 0) return fromUserInput;

      const reason = testTypeToReasonOption(prefilledData?.testType);
      if (reason) return new Set<ReasonOption>([reason]);

      return new Set<ReasonOption>();
    }
  );

  /** Avoid re-applying the default after the user clears all reasons on this intake. */
  const appliedDefaultKeyRef = useRef<string | null>(null);

  useEffect(() => {
    appliedDefaultKeyRef.current = null;
  }, [orderUidPublic, testUid]);

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  // prefilledData often arrives after this step mounts; sync checkbox once per intake.
  useEffect(() => {
    if (userInputFields.reasonForAppointment) return;

    const reason = testTypeToReasonOption(prefilledData?.testType);
    if (!reason) return;

    const dedupeKey = `${orderUidPublic ?? ""}:${testUid ?? ""}:${reason}`;
    if (appliedDefaultKeyRef.current === dedupeKey) return;
    appliedDefaultKeyRef.current = dedupeKey;

    const preset = new Set<ReasonOption>([reason]);
    setSelectedOptions(preset);
    updateUserField("reasonForAppointment", Array.from(preset).join(","));
  }, [
    orderUidPublic,
    testUid,
    prefilledData?.testType,
    userInputFields.reasonForAppointment,
    updateUserField,
  ]);

  const handleOptionChange = (value: ReasonOption, checked: boolean) => {
    const updated = new Set(selectedOptions);
    if (checked) {
      updated.add(value);
    } else {
      updated.delete(value);
    }
    setSelectedOptions(updated);
    updateUserField(
      "reasonForAppointment",
      updated.size > 0 ? Array.from(updated).join(",") : null
    );
  };

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleContinue = () => {
    if (selectedOptions.size === 0) return;
    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  const options: { value: ReasonOption; label: string }[] = [
    {
      value: "signatera",
      label: intl.formatMessage(messages.optionSignatera),
    },
    {
      value: "altera",
      label: intl.formatMessage(messages.optionAltera),
    },
    {
      value: "latitude",
      label: intl.formatMessage(messages.optionLatitude),
    },
    {
      value: "empower",
      label: intl.formatMessage(messages.optionEmpower),
    },
  ];

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={selectedOptions.size === 0}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__checkbox-list">
        {options.map((option) => (
          <InlineCheckbox
            key={option.value}
            id={`reason-${option.value}`}
            label={option.label}
            checked={selectedOptions.has(option.value)}
            onChange={(checked) => handleOptionChange(option.value, checked)}
          />
        ))}
      </div>
    </IntakeFormStepLayout>
  );
};

export default IntakeStepReason;
